import React, { useCallback, useMemo } from 'react';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import { InputLabel, Switch } from '@material-ui/core';
import useStyles from './ClientEditionForm.styles';
import HeaderActions from './header/HeaderActions';
import { ClientExact } from '../../models/Client';
import TextField from '../../../common/components/fields/TextField';
import clientEditionFormSchema from '../../schemas/clientEditionFormSchema';
import IRTHConfig from './irth-config/IRTHConfig';
import { useAbilityContext } from '../../../common/contexts/abilityContext';
import UserList from './list/user/UserList';
import PilotList from './list/pilot/PilotList';

const ClientEditionForm = ({ client, onBackClick, onSubmit }) => {
  const classes = useStyles();
  const context = useAbilityContext();

  const handleSubmit = useCallback(
    async (values, actions) => {
      const filteredValues = values;
      filteredValues.users.pop();
      filteredValues.pilots.pop();
      await onSubmit(filteredValues, actions);
      actions.setSubmitting(false);
    },
    [onSubmit]
  );

  const checkIfLastUserIsEmpty = useCallback(() => {
    if (!client.users || client.users.length === 0) return false;
    const lastUser = client.users[client.users.length - 1];
    return lastUser && lastUser.name === '' && lastUser.email === '' && lastUser.description === '';
  }, [client.users]);

  const checkIfLastPilotIsEmpty = useCallback(() => {
    if (!client.pilots || client.pilots.length === 0) return false;
    const lastPilot = client.pilots[client.pilots.length - 1];
    return lastPilot && lastPilot.name === '' && lastPilot.licenseNumber === '';
  }, [client.pilots]);

  const initialValues = useMemo(() => {
    if (checkIfLastUserIsEmpty() && checkIfLastPilotIsEmpty()) return client;
    const newUsers = client.users || [];
    const newPilots = client.pilots || [];
    newUsers.push({ name: '', email: '', description: '' });
    newPilots.push({ name: '', licenseNumber: '' });
    return { ...client, users: newUsers, pilots: newPilots };
  }, [checkIfLastUserIsEmpty, checkIfLastPilotIsEmpty, client]);

  return (
    <Formik initialValues={initialValues} validationSchema={clientEditionFormSchema} onSubmit={handleSubmit}>
      {({ setFieldValue, values }) => (
        <Form>
          <div className={classes.header}>
            <div className={classes.label}>
              <TextField name="name" placeholder={i18n.t('client.form.clientName')} classes={{ root: classes.textField }} inputClassName={classes.textField} />
            </div>
            <HeaderActions onBackClick={onBackClick} />
          </div>
          <span>
            {values?.irthConfig?.isActive && (
              <>
                <div className={classes.textHeader}>{i18n.t('client.form.irthConfig')}</div>
                <div className={classes.scrollableContainer}>
                  <IRTHConfig />
                </div>
              </>
            )}
          </span>
          <div className={classes.textHeader}>{i18n.t('client.form.description')}</div>
          <TextField className={classes.container} name="description" placeholder={i18n.t('client.form.enterDescription')} />
          <div className={classes.textHeader}>{i18n.t('client.user')}</div>
          <div className={classes.scrollableContainer}>
            <UserList />
          </div>
          <div className={classes.textHeader}>{i18n.t('client.pilot')}</div>
          <div className={classes.scrollableContainer}>
            <PilotList />
          </div>
          <div className={classes.textHeader}>{i18n.t('client.configuration')}</div>
          <div className={classes.scrollableContainerColumn}>
            <div>
              {context.can('manage', 'IRTH') && (
                <div className={classes.irthConfig}>
                  <InputLabel id="irth-integration-label">IRTH Integration: </InputLabel>
                  <div className={classes.headerSwitch}>
                    <InputLabel id="irth-integration-label">OFF</InputLabel>
                    <Switch
                      color="primary"
                      checked={values?.irthConfig?.isActive ?? false}
                      name="irthConfig.isActive"
                      onChange={(event, checked) => {
                        setFieldValue('irthConfig.isActive', checked);
                      }}
                    />
                    <InputLabel id="irth-integration-label">ON</InputLabel>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

ClientEditionForm.propTypes = {
  client: ClientExact.isRequired,
  onBackClick: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

ClientEditionForm.defaultProps = {};
export default ClientEditionForm;
